<template>
  <div class="min-h-screen bg-white relative flex flex-col">
    <GradientHalo />

    <!-- Header -->
    <header class="relative flex items-center justify-between px-6 md:px-10 pt-6 md:pt-8">
      <div class="flex items-center gap-3">
        <img class="h-8 w-auto" src="@/assets/large_logo_estaly_black.png" alt="Estaly" />
        <span
          class="inline-flex items-center rounded-full bg-primary-light text-primary-dark text-xs font-semibold px-3 py-1"
        >
          {{ t("merchant_space") }}
        </span>
      </div>
    </header>

    <!-- Main -->
    <main class="relative flex-1 flex items-center justify-center px-4 py-8 md:py-0">
      <ModernCard>
        <h1 class="text-2xl font-bold text-gray-900 leading-tight">{{ t("title") }}</h1>
        <p class="mt-2 text-sm text-gray-500 leading-relaxed">{{ t("subtitle") }}</p>

        <VeeForm v-slot="{ handleSubmit }" :validation-schema="schema" as="div" class="mt-8">
          <form @submit="handleSubmit($event, handleResetPassword)" novalidate>
            <ModernFormField
              id="password"
              name="password"
              :type="passwordVisible ? 'text' : 'password'"
              autocomplete="new-password"
              v-model="password"
              :label="t('password_label')"
              :placeholder="t('password_placeholder')"
            >
              <template #suffix>
                <PasswordToggleButton
                  :visible="passwordVisible"
                  :show-label="t('show_password')"
                  :hide-label="t('hide_password')"
                  @toggle="passwordVisible = !passwordVisible"
                />
              </template>
            </ModernFormField>

            <ModernFormField
              id="passwordConfirmation"
              name="passwordConfirmation"
              :type="confirmPasswordVisible ? 'text' : 'password'"
              autocomplete="new-password"
              v-model="passwordConfirmation"
              :label="t('password_confirmation_label')"
              :placeholder="t('password_confirmation_placeholder')"
            >
              <template #suffix>
                <PasswordToggleButton
                  :visible="confirmPasswordVisible"
                  :show-label="t('show_password')"
                  :hide-label="t('hide_password')"
                  @toggle="confirmPasswordVisible = !confirmPasswordVisible"
                />
              </template>
            </ModernFormField>

            <ModernButton :loading="isLoading">
              {{ t("submit") }}
            </ModernButton>
          </form>
        </VeeForm>
      </ModernCard>
    </main>

    <!-- Footer -->
    <footer class="relative text-center py-6 text-sm text-gray-400">
      {{ t("need_help") }}
      <a href="mailto:contact@estaly.co" class="text-gray-500 underline hover:text-gray-900 transition-colors">
        {{ t("contact_us") }}
      </a>
    </footer>
  </div>
</template>

<script>
import { Form as VeeForm } from "vee-validate";
import * as yup from "yup";
import { mapActions, mapState, mapGetters } from "vuex";
import { useI18n } from "vue-i18n";
import { ModernButton, ModernFormField, ModernCard, GradientHalo, PasswordToggleButton } from "@estaly/ui";

export default {
  components: {
    VeeForm,
    ModernButton,
    ModernFormField,
    ModernCard,
    GradientHalo,
    PasswordToggleButton,
  },

  setup() {
    const { t } = useI18n();
    return { t };
  },

  data() {
    return {
      password: "",
      passwordConfirmation: "",
      passwordVisible: false,
      confirmPasswordVisible: false,
      isLoading: false,
      email: this.$route.query.email,
      resetPasswordToken: this.$route.params.id,
    };
  },

  computed: {
    ...mapState("auth", ["signedIn", "currentUser"]),
    ...mapGetters("auth", ["isMerchantProfile", "hasAccessToAdminDashboard", "isSellerOrResponsible"]),
    schema() {
      return yup.object({
        password: yup.string().required(this.t("password_required")).min(8, this.t("password_too_short")),
        passwordConfirmation: yup
          .string()
          .oneOf([yup.ref("password"), ""], this.t("passwords_must_match"))
          .required(this.t("password_confirmation_required")),
      });
    },
  },

  created() {
    if (this.signedIn) this.$router.push("/");
  },

  methods: {
    ...mapActions("notifications", ["notify"]),
    ...mapActions("auth", ["resetPassword", "signout"]),
    ...mapActions("store", ["updateStoreId"]),

    async handleResetPassword() {
      if (this.isLoading) return;
      this.isLoading = true;

      let payload = {
        email: this.email,
        user: {
          password: this.password,
          password_confirmation: this.passwordConfirmation,
        },
      };
      const response = await this.resetPassword({ resetPasswordToken: this.resetPasswordToken, payload });
      if (!response?.success) {
        this.isLoading = false;
        return;
      }

      if (this.hasAccessToAdminDashboard) return this.$router.push("/admin/home");
      if (this.isSellerOrResponsible) {
        this.updateStoreId(this.currentUser.storeIds[0]);
        return this.$router.push("/pos/home");
      }
      if (!this.isMerchantProfile) {
        this.notify({ type: "error", category: "simple", title: this.t("wrong_url") });
        return await this.signout();
      }
    },
  },
};
</script>

<i18n src="./PasswordReset.i18n.json"></i18n>
